import { useEffect } from "react";
import {
    Alert,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { UPLOADER_CHUNKED, UPLOADER_LEGACY } from "@/Service/UploaderToggle";
import { useSystemSettingsActions, useSystemSettingsState } from "@/store";

// System-wide uploader mode. Compact toggle-button pair replaces the old
// radio-group-with-two-paragraphs layout (2026-08-06 UX redesign) — full
// context is behind the info tooltip so the row stays visually terse.
export const UploaderToggleSection = () => {
    const { activeUploader, fetch, update } = useSystemSettingsState();
    const { fetchActiveUploader, updateActiveUploader } = useSystemSettingsActions();

    useEffect(() => {
        fetchActiveUploader();
    }, [fetchActiveUploader]);

    const isLoading = fetch.status === "loading" || update.status === "loading";

    const handleChange = (_e, next) => {
        if (!next || next === activeUploader) return;
        updateActiveUploader(next);
    };

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Uploader mode
                    </Typography>
                    <Tooltip
                        arrow
                        title={
                            <>
                                System-wide, applies to every user. <b>Chunked</b> uses
                                the TUS resumable protocol — survives flaky and mobile
                                networks. <b>Legacy</b> is the single-request multipart
                                endpoint. Flip to Legacy as a kill switch if chunked
                                starts failing. In-flight uploads finish on their current
                                mode; only new uploads pick up the change.
                            </>
                        }
                    >
                        <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ color: "text.secondary", cursor: "help" }}
                        />
                    </Tooltip>
                </Stack>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={activeUploader}
                    onChange={handleChange}
                    disabled={isLoading}
                >
                    <ToggleButton value={UPLOADER_LEGACY}>Legacy</ToggleButton>
                    <ToggleButton value={UPLOADER_CHUNKED}>Chunked</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            <Typography variant="caption" color="text.secondary">
                system-wide
            </Typography>

            {fetch.status === "failed" && (
                <Alert severity="error" variant="outlined">
                    Failed to load the current uploader setting. Showing the last cached value.
                </Alert>
            )}
        </Stack>
    );
};

export default UploaderToggleSection;
